import {
  forwardRef,
  type InputHTMLAttributes,
  type JSX,
  type LabelHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../../lib/utils.ts';

const fieldClass = cn(
  'w-full rounded-md border bg-background px-3 py-2 text-sm',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

function withFieldClass(className: string | undefined, error: string | null | undefined): string {
  return cn(fieldClass, error ? 'border-destructive' : undefined, className);
}

function FieldErrorText({ error }: { readonly error?: string | null }): JSX.Element | null {
  if (!error) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-destructive">
      {error}
    </p>
  );
}

export type LabelProps = LabelHTMLAttributes<HTMLLabelElement> & {
  readonly htmlFor: string;
  readonly 'data-testid'?: string;
};

export function Label({ className, ...props }: LabelProps): JSX.Element {
  return <label className={cn('text-sm font-medium', className)} {...props} />;
}

/**
 * The three controls forward their ref: react-hook-form's `register()` spreads a
 * `ref` onto the element, and React 18 DROPS a ref passed to a plain function
 * component - silently, so an uncontrolled field would lose defaultValues
 * population and shouldFocusError with nothing to see. Keep the forwardRef when
 * editing these.
 */
export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  readonly error?: string | null;
  readonly 'data-testid'?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, error, ...props },
  ref,
) {
  return (
    <>
      <input
        ref={ref}
        className={withFieldClass(className, error)}
        aria-invalid={error ? true : undefined}
        {...props}
      />
      <FieldErrorText error={error} />
    </>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  readonly error?: string | null;
  readonly 'data-testid'?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, error, ...props },
  ref,
) {
  return (
    <>
      <textarea
        ref={ref}
        className={withFieldClass(className, error)}
        aria-invalid={error ? true : undefined}
        {...props}
      />
      <FieldErrorText error={error} />
    </>
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  readonly error?: string | null;
  readonly 'data-testid'?: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, error, ...props },
  ref,
) {
  return (
    <>
      <select
        ref={ref}
        className={withFieldClass(className, error)}
        aria-invalid={error ? true : undefined}
        {...props}
      />
      <FieldErrorText error={error} />
    </>
  );
});
